import {
  Badge,
  Body1,
  Body1Strong,
  Button,
  Caption1,
  Link as FluentLink,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { ArrowLeftFilled } from '@fluentui/react-icons';

import { AI_FINDING_PRIORITY_META, AI_FINDING_STATUS_META } from '../constants';
import { getAiFindingSummary } from '../helpers';
import type { TAiFinding, TAiFindingAction } from '../types';
import FindingText from './FindingText';

const useStyles = makeStyles({
  detail: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  backButton: {
    alignSelf: 'flex-start',
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  badgeRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS,
  },
  summaryCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingHorizontalL,
    borderRadius: tokens.borderRadiusLarge,
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  description: {
    color: tokens.colorNeutralForeground2,
    overflowWrap: 'anywhere',
  },
  impactRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    paddingTop: tokens.spacingVerticalXS,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  actions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  affectedDocsList: {
    margin: 0,
    paddingLeft: tokens.spacingHorizontalL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  affectedDocsItem: {
    overflowWrap: 'anywhere',
  },
  affectedDocsLink: {
    textDecorationLine: 'underline',
    textUnderlineOffset: '2px',
    textDecorationThickness: '1px',
  },
});

type TProps = {
  finding: TAiFinding;
  onBack: () => void;
  onActionClick: (action: TAiFindingAction) => void;
};

const MockAiFindingDetail = ({ finding, onBack, onActionClick }: TProps) => {
  const classes = useStyles();
  const priorityMeta = AI_FINDING_PRIORITY_META[finding.priority];
  const statusMeta = finding.status ? AI_FINDING_STATUS_META[finding.status.tone] : undefined;

  return (
    <div className={classes.detail}>
      <Button
        size="small"
        appearance="subtle"
        icon={<ArrowLeftFilled />}
        onClick={onBack}
        className={classes.backButton}
      >
        Back
      </Button>

      <div className={classes.header}>
        <div className={classes.badgeRow}>
          <Badge appearance="tint" color={priorityMeta.badgeColor}>
            {priorityMeta.label}
          </Badge>
          {finding.status && statusMeta ? (
            <Badge appearance="outline" color={statusMeta.badgeColor}>
              {finding.status.label}
            </Badge>
          ) : null}
        </div>
        <Body1Strong>{finding.title}</Body1Strong>
      </div>

      <div className={classes.summaryCard}>
        <Caption1>Summary</Caption1>
        <Body1>
          <FindingText text={getAiFindingSummary(finding)} />
        </Body1>
        <Body1 className={classes.description}>
          <FindingText text={finding.description} />
        </Body1>
        {finding.impact ? (
          <div className={classes.impactRow}>
            <Caption1>Impact</Caption1>
            <Body1Strong>{finding.impact}</Body1Strong>
          </div>
        ) : null}
      </div>

      <div className={classes.section}>
        <Caption1>Suggested actions</Caption1>
        <div className={classes.actions}>
          {finding.suggestedActions.map((action) => (
            <Button
              key={action.label}
              size="small"
              appearance={action.type === 'primary' ? 'primary' : 'secondary'}
              onClick={() => onActionClick(action)}
            >
              {action.label}
            </Button>
          ))}
        </div>
      </div>

      <div className={classes.section}>
        <Caption1>Affected documents</Caption1>
        <ul className={classes.affectedDocsList}>
          {finding.affectedDocs.map((item) => {
            const affectedDocument = typeof item === 'string' ? { label: item } : item;

            return (
              <li key={`${affectedDocument.label}-${affectedDocument.href ?? ''}`}>
                <Body1 className={classes.affectedDocsItem}>
                  {affectedDocument.href ? (
                    <FluentLink
                      className={classes.affectedDocsLink}
                      href={affectedDocument.href}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <FindingText text={affectedDocument.label} />
                    </FluentLink>
                  ) : (
                    <FindingText text={affectedDocument.label} />
                  )}
                </Body1>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default MockAiFindingDetail;
